import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Gift, Users, Copy, Check, Share2, Award, Trophy, Sparkles, Star, Crown
} from 'lucide-react';

const Referral = () => {
  const { user, token } = useAuth();
  const [referralInfo, setReferralInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const fetchReferralInfo = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [infoRes, leaderRes] = await Promise.all([
        axios.get(`${API}/invite-shine/info`, { headers }),
        axios.get(`${API}/invite-shine/leaderboard`, { headers })
      ]);
      setReferralInfo(infoRes.data);
      setLeaderboard(leaderRes.data.leaderboard);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (referralInfo?.referral_link) {
      navigator.clipboard.writeText(`https://${referralInfo.referral_link}`);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = async () => {
    if (navigator.share && referralInfo) {
      try {
        await navigator.share({
          title: 'Join Clierxter Vision Lab',
          text: 'Transform your brand with AI-powered tools. Use my referral link to get started!',
          url: `https://${referralInfo.referral_link}`
        });
      } catch (error) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  // Map reward types to icons
  const rewardIcons = {
    content_credit: Gift,
    badge: Star,
    premium_days: Award,
    pro_days: Crown,
    vip: Sparkles
  };

  // Default rewards with icons
  const defaultRewards = [
    { threshold: 1, reward: '1 bonus content idea', icon: Gift },
    { threshold: 3, reward: 'Exclusive Clierxter badge', icon: Star },
    { threshold: 5, reward: '1 week Premium access', icon: Award },
    { threshold: 10, reward: '1 month Premium access', icon: Trophy },
    { threshold: 25, reward: '3 months Pro access', icon: Crown },
    { threshold: 50, reward: 'Lifetime VIP status', icon: Sparkles }
  ];

  // Use rewards from API with icons, or fallback
  const rewards = referralInfo?.rewards_structure 
    ? referralInfo.rewards_structure.map((r, i) => ({
        ...r,
        icon: rewardIcons[r.reward_type] || defaultRewards[i]?.icon || Gift
      }))
    : defaultRewards;

  if (loading) {
    return (
      <DashboardLayout title="Invite & Shine">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-[#E5C585]">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Invite & Shine">
      <div className="max-w-6xl mx-auto">
        {/* Header Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
          
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="text-[#D4AF37]" size={16} />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">INVITE & SHINE</span>
              </div>
              
              <h2 className="font-serif text-3xl text-white mb-4">Shine Together</h2>
              <p className="text-[#A1A1AA] mb-6 font-light leading-relaxed">
                Invite visionary women to Clierxter Vision Lab. When they shine, you shine too. 
                Earn rewards and grow together.
              </p>
              
              <div className="flex gap-3">
                <Button onClick={copyLink} className="btn-outline-gold px-6 py-3" data-testid="copy-referral-btn">
                  {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
                <Button onClick={shareLink} className="btn-gold px-6 py-3" data-testid="share-referral-btn">
                  <Share2 size={16} className="mr-2" />
                  Share
                </Button>
              </div>
            </div>

            <div className="text-center">
              <div className="inline-block">
                <div className="font-serif text-6xl text-white mb-2">{referralInfo?.total_referrals || 0}</div>
                <p className="text-[#E5C585] font-accent text-xs tracking-wider">TOTAL REFERRALS</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Referral Link */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-6 mb-8">
          <label className="text-[#A1A1AA] text-sm mb-3 block">Your Unique Referral Link</label>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-[#0A0A0A] border border-[#27272A] px-4 py-3 text-[#E5C585] font-mono text-sm">
              https://{referralInfo?.referral_link}
            </div>
            <Button onClick={copyLink} className="btn-gold px-6 py-3" data-testid="copy-link-full-btn">
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </Button>
          </div>
          <p className="text-[#52525B] text-xs mt-3">
            Your referral code: <span className="text-[#E5C585]">{referralInfo?.referral_code}</span>
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-luxury p-6 text-center">
            <Users className="text-[#E5C585] mx-auto mb-3" size={32} />
            <div className="font-serif text-3xl text-white mb-1">{referralInfo?.total_referrals || 0}</div>
            <p className="text-[#52525B] text-sm">Invitations envoyées</p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-luxury p-6 text-center">
            <Award className="text-[#D4AF37] mx-auto mb-3" size={32} />
            <div className="font-serif text-3xl text-white mb-1">
              {rewards.filter(r => (referralInfo?.total_referrals || 0) >= r.threshold).length}
            </div>
            <p className="text-[#52525B] text-sm">Récompenses débloquées</p>
          </motion.div>
        </div>

        {/* Founding Member Section - Benefits revealed later */}
        {user?.is_founding_member && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.45 }} 
            className="card-gold p-8 mb-8 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-[#D4AF37]/20 to-transparent rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Crown className="text-[#D4AF37]" size={24} />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">FOUNDING MEMBER</span>
              </div>

              <p className="text-white text-xl font-serif mb-4">
                Tu fais partie des premières.
              </p>
              
              <p className="text-[#A1A1AA] text-sm italic mb-6 max-w-md mx-auto">
                En tant que Founding Member, des privilèges exclusifs t'attendent.
              </p>
              
              <p className="text-[#71717A] text-xs">
                Révélés en temps voulu.
              </p>
            </div>
          </motion.div>
        )}

        {/* Rewards Tiers */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="text-[#D4AF37]" size={24} />
            <h3 className="font-serif text-xl text-white">Rewards Tiers</h3>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {rewards.map((reward, index) => {
              const Icon = reward.icon;
              const isUnlocked = (referralInfo?.total_referrals || 0) >= reward.threshold;
              
              return (
                <div 
                  key={index} 
                  className={`p-6 text-center transition-all ${
                    isUnlocked 
                      ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/40' 
                      : 'bg-[#0A0A0A] border border-[#27272A]'
                  }`}
                >
                  <Icon className={`mx-auto mb-3 ${isUnlocked ? 'text-[#D4AF37]' : 'text-[#27272A]'}`} size={28} />
                  <div className={`font-serif text-2xl mb-1 ${isUnlocked ? 'text-[#E5C585]' : 'text-[#52525B]'}`}>
                    {reward.threshold}+
                  </div>
                  <p className={`text-xs ${isUnlocked ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`}>
                    {reward.reward}
                  </p>
                  {isUnlocked && (
                    <span className="inline-block mt-2 px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-accent tracking-wider">
                      UNLOCKED
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mt-8">
            <div className="card-premium p-8">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">Top Ambassadors</h3>
              </div>
              
              <div className="space-y-3">
                {leaderboard.map((leader, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#27272A]">
                    <div className="flex items-center gap-4">
                      <span className={`w-8 h-8 flex items-center justify-center font-serif ${
                        index === 0 ? 'bg-[#D4AF37] text-black' :
                        index === 1 ? 'bg-[#A1A1AA] text-black' :
                        index === 2 ? 'bg-[#8B7355] text-white' :
                        'bg-[#27272A] text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex items-center gap-3">
                        {leader.picture ? (
                          <img src={leader.picture} alt={leader.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                            <span className="text-[#E5C585] font-serif">{leader.name?.charAt(0)}</span>
                          </div>
                        )}
                        <span className="text-white">{leader.name}</span>
                      </div>
                    </div>
                    <span className="text-[#D4AF37] font-serif">{leader.referral_count} referrals</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Recent Referrals */}
        {referralInfo?.referrals && referralInfo.referrals.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">YOUR REFERRALS</span>
            </div>
            
            <div className="space-y-2">
              {referralInfo.referrals.map((referral, index) => (
                <div key={index} className="card-luxury p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                      <span className="text-[#E5C585] font-serif">{referral.name?.charAt(0) || '?'}</span>
                    </div>
                    <div>
                      <span className="text-white">{referral.name || 'Anonymous'}</span>
                      {referral.subscription_tier && referral.subscription_tier !== 'free' && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37]">{referral.subscription_tier}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[#52525B] text-sm">
                    {new Date(referral.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* How it Works */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">HOW IT WORKS</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-premium p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-[#E5C585] to-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-serif text-xl">1</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">Share Your Light</h4>
              <p className="text-[#52525B] text-sm font-light">
                Copy your unique referral link and share it with visionary women.
              </p>
            </div>
            
            <div className="card-premium p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-[#E5C585] to-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-serif text-xl">2</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">They Join & Transform</h4>
              <p className="text-[#52525B] text-sm font-light">
                When they sign up, they start their brand transformation journey.
              </p>
            </div>
            
            <div className="card-premium p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-[#E5C585] to-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-serif text-xl">3</span>
              </div>
              <h4 className="font-serif text-lg text-white mb-2">Shine Together</h4>
              <p className="text-[#52525B] text-sm font-light">
                You both earn rewards. Rising together, shining together.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Referral;
